import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'

import useApiHelpers from 'lib/effects/api-helpers'
import { noop } from 'lib/functions/noop'
import { Task, TaskAttributes } from 'lib/models/task'
import { TaskEvent } from 'lib/models/task-event'
import { ProductsWithTasks } from './types'
import { TaskStage } from 'lib/models/task-stages'

type TasksContextType = {
  tasks: Task[]
  product: ProductsWithTasks | null
  actionTask: (
    taskId: number,
    actionName: string,
    attributes?: Record<string, string>
  ) => void
  createTask: () => void
  isCreatingTask: boolean
  deleteTask: (taskId: number) => void
  fetchTasks: ({
    queryParams,
  }: {
    queryParams?: Record<string, number | string | boolean>
  }) => void
  isFetchingTasks: boolean
  updateTask: (taskId: number, attributes: Partial<TaskAttributes>) => void
}

type TaskStagesContextType = {
  taskStages: TaskStage[]
  fetchTaskStages: () => void
  isFetchingTaskStages: boolean
}

type TaskEventsContextType = {
  taskEvents: TaskEvent[]
  fetchTaskEvents: ({
    queryParams,
  }: {
    queryParams?: Record<string, number | string>
  }) => void
  queryParams?: Record<string, number | string>
  isFetchingTaskEvents: boolean
}

const defaultTasksContext: TasksContextType = {
  tasks: [],
  product: null,
  actionTask: noop,
  createTask: noop,
  isCreatingTask: false,
  deleteTask: noop,
  fetchTasks: noop,
  isFetchingTasks: false,
  updateTask: noop,
}

const TasksContext = createContext<TasksContextType>(defaultTasksContext)
const TaskEventsContext = createContext<TaskEventsContextType>(
  {} as TaskEventsContextType
)
const TaskStagesContext = createContext<TaskStagesContextType | undefined>(
  undefined
)

const useTasksContext = ({
  enabled = true,
}: {
  enabled?: boolean
} = {}): TasksContextType => {
  const context = useContext(TasksContext)

  // We call useTasksContext in a shared component (Activity) that is used
  // across multiple products. Since we can't conditionally call hooks, we
  // use this wrapper to avoid loading tasks for products that don't use them
  // (e.g. Funeral plans).
  if (!enabled) {
    return defaultTasksContext
  }

  if (context.product === null) {
    throw new Error(
      'useTasksContext was called with `enabled=true` but no TasksProvider exists in the tree'
    )
  }

  return context
}

const TasksProvider = ({
  children,
  product,
}: {
  children: ReactNode
  product: ProductsWithTasks
}) => {
  const {
    items: tasks,
    fetchItems: fetchTasks,
    isFetching: isFetchingTasks,
    updateItem: updateTask,
    isUpdating: isUpdatingTask,
    createItem: createTask,
    isCreating: isCreatingTask,
    actionItem: actionTask,
    deleteItem: deleteTask,
  } = useApiHelpers({
    baseUrl: `/api/tasks`,
    type: 'tasks',
  })

  const value = {
    tasks,
    fetchTasks,
    isFetchingTasks,
    updateTask,
    isUpdatingTask,
    createTask,
    isCreatingTask,
    actionTask,
    deleteTask,
    product,
  }

  return <TasksContext.Provider value={value}>{children}</TasksContext.Provider>
}

const useTaskEventsContext = () => {
  const context = useContext(TaskEventsContext)
  if (context === undefined) {
    throw new Error(
      'useTaskEventsContext must be used within an TaskEventsProvider'
    )
  }
  return context
}

const TaskEventsProvider = ({ children }: { children: ReactNode }) => {
  const {
    items: taskEvents,
    fetchItems: fetchTaskEvents,
    isFetching: isFetchingTaskEvents,
  } = useApiHelpers({
    baseUrl: `/api/task-events`,
    type: 'task_events',
  })

  const value = {
    taskEvents,
    fetchTaskEvents,
    isFetchingTaskEvents,
  }

  return (
    <TaskEventsContext.Provider value={value}>
      {children}
    </TaskEventsContext.Provider>
  )
}

const useTaskStagesContext = (): TaskStagesContextType => {
  const context = useContext(TaskStagesContext)

  if (context === undefined) {
    throw new Error(
      'useTaskStageContext must be used within an TaskStageProvider'
    )
  }

  return context
}

const TaskStagesProvider = ({
  children,
  probateCaseId,
}: {
  children: ReactNode
  probateCaseId: number
}) => {
  const {
    items: taskStages,
    fetchItems: fetchTaskStages,
    isFetching: isFetchingTaskStages,
  } = useApiHelpers({
    baseUrl: `/api/probate-cases/${probateCaseId}/task-stages`,
    type: 'tasks',
  })

  const value = {
    taskStages,
    fetchTaskStages,
    isFetchingTaskStages,
  }

  return (
    <TaskStagesContext.Provider value={value}>
      {children}
    </TaskStagesContext.Provider>
  )
}

export {
  TasksProvider,
  useTasksContext,
  TaskEventsProvider,
  useTaskEventsContext,
  TaskStagesProvider,
  useTaskStagesContext,
}
